import {
  faBan,
  faBolt,
  faClipboard,
  faEnvelope,
  faPencil,
  faThumbsUp,
  faTrashAlt,
  faUserPlus,
  faUserMinus,
} from '@fortawesome/free-solid-svg-icons'
import { DateTime } from 'luxon'
import React, { useContext, useRef, useState, useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import { useNavigate, useParams } from 'react-router'
import { randomColor } from 'utils'
import {
  Avatar,
  Content,
  Card,
  Data,
  Indicators,
  Layout,
  Modal,
  Navigation,
  Responsive,
} from 'ui'
import { usePaginatedProfiles } from './usePaginatedProfiles'
import { UserProfileNavigationDrawer } from './UserProfileNavigationDrawer'
import {
  useAdminProfileById,
  AdminProfileDetail,
} from '../../../queries/useAdminProfileById'
import { useProfileMatches } from '../../../queries/useProfileMatches'
import {
  useActivateProfileMutation,
  useApproveProfileMutation,
  useDeactivateProfileMutation,
  useDestroyProfileMutation,
  useSuspendProfileMutation,
} from '../../../queries/useAdminProfileMutations'
import {
  useUserByProfileId,
  useDemoteUser,
  usePromoteUser,
} from '../../../queries'
import { MatchesList } from '../../../components/MatchesList'
import { UserProfileDenyView } from './UserProfileDenyView'

const { Attribute, Section } = Data

export const UserProfileDetailView: React.FC = () => {
  const params = useParams<{ id: string }>()
  const id = params.id ?? ''

  const navigate = useNavigate()

  // Drawer appearance
  const { useDisplayMode } = useContext(Responsive.DrawerManagerContext)
  useDisplayMode('compact')

  const { locationSearch } = usePaginatedProfiles()

  const [updatingProfile, setUpdatingProfile] = useState(false)
  const [confirmId, setConfirmId] = useState(null as null | string)
  const [showDenyModal, setShowDenyModal] = useState(false)
  const [confirmRoleAction, setConfirmRoleAction] = useState<
    'promote' | 'demote' | null
  >(null)
  const [generalErrorMessage, setGeneralErrorMessage] = useState(
    null as null | string
  )

  // Use our new react-query hooks instead of Apollo
  const {
    data: profile,
    isLoading: loading,
    error: profileError,
  } = useAdminProfileById(id)

  const {
    mutate: approveProfile,
    isPending: isApproving,
    error: approveError,
  } = useApproveProfileMutation()

  const {
    mutate: suspendProfile,
    isPending: isSuspending,
    error: suspendError,
  } = useSuspendProfileMutation()

  const {
    mutate: activateProfile,
    isPending: isActivating,
    error: activateError,
  } = useActivateProfileMutation()

  const {
    mutate: deactivateProfile,
    isPending: isDeactivating,
    error: deactivateError,
  } = useDeactivateProfileMutation()

  const {
    mutate: destroyProfile,
    isPending: isDestroying,
    error: destroyError,
  } = useDestroyProfileMutation()

  // Add user lookup by profile ID
  const {
    data: userInfo,
    isLoading: loadingUser,
    error: userError,
  } = useUserByProfileId(id)

  // Add role change mutations
  const promoteUserMutation = usePromoteUser()
  const demoteUserMutation = useDemoteUser()

  const mutationError =
    approveError ||
    suspendError ||
    activateError ||
    deactivateError ||
    destroyError

  const handleApproveClick = async () => {
    setUpdatingProfile(true)
    approveProfile(id, {
      onSettled: () => {
        setUpdatingProfile(false)
      },
    })
  }

  const handleActivateClick = async () => {
    setUpdatingProfile(true)
    activateProfile(id, {
      onSettled: () => {
        setUpdatingProfile(false)
      },
    })
  }

  const handleSuspendClick = async () => {
    setUpdatingProfile(true)
    suspendProfile(id, {
      onSettled: () => {
        setUpdatingProfile(false)
      },
    })
  }

  const handleDeactivateClick = async () => {
    setUpdatingProfile(true)
    deactivateProfile(id, {
      onSettled: () => {
        setUpdatingProfile(false)
      },
    })
  }

  const handleDestroyClick = async () => {
    setConfirmId(id)
  }

  const confirmDestroyProfile = async () => {
    setUpdatingProfile(true)
    dismissModal()
    destroyProfile(id, {
      onSuccess: (data) => {
        if (!data.success && data.errors && data.errors.length > 0) {
          setGeneralErrorMessage(data.errors[0]?.message ?? '')
          setUpdatingProfile(false)
        } else {
          // Pass excludeId to filter out the deleted profile from search results
          // (handles race condition where Algolia index hasn't updated yet)
          const separator = locationSearch ? '&' : '?'
          navigate(
            `/admin/profiles${locationSearch}${separator}excludeId=${id}`
          )
        }
      },
      onError: (error) => {
        setGeneralErrorMessage(error.message || 'An error occurred')
        setUpdatingProfile(false)
      },
    })
  }

  const dismissModal = () => {
    setConfirmId(null)
  }

  const handleDenyClick = () => {
    setShowDenyModal(true)
  }

  const handleDenyModalDismiss = () => {
    setShowDenyModal(false)
  }

  const handleEditClick = () => {
    navigate(`/admin/profiles/edit/${id}${locationSearch}`)
  }

  // Add role change handlers
  const showPromoteConfirmation = () => setConfirmRoleAction('promote')
  const showDemoteConfirmation = () => setConfirmRoleAction('demote')
  const dismissRoleModal = () => setConfirmRoleAction(null)

  const handleConfirmRoleChange = async () => {
    if (!userInfo?.id) return

    setUpdatingProfile(true)
    try {
      if (confirmRoleAction === 'promote') {
        await promoteUserMutation.mutateAsync(userInfo.id)
      } else if (confirmRoleAction === 'demote') {
        await demoteUserMutation.mutateAsync(userInfo.id)
      }
      dismissRoleModal()
    } catch (error) {
      console.error('Role change failed:', error)
    } finally {
      setUpdatingProfile(false)
    }
  }

  const isRoleMutating =
    promoteUserMutation.isPending || demoteUserMutation.isPending
  const isCurrentlyAdmin = userInfo?.accessLevel === 'ADMIN'

  const inviteUrlRef = useRef(null as null | HTMLInputElement)
  const handleCopyInvite = () => {
    inviteUrlRef?.current?.select()
    document.execCommand('copy')
  }

  const handleSelectMatch = (profileMatch: any) =>
    navigate(
      `/admin/profiles/show/${profileMatch.matchedProfileId}${locationSearch}`
    )

  // Track if any mutation is in progress
  const isMutating =
    isApproving ||
    isSuspending ||
    isActivating ||
    isDeactivating ||
    isDestroying ||
    isRoleMutating

  useEffect(() => {
    if (updatingProfile || isMutating) {
      setGeneralErrorMessage(null)
    }
  }, [setGeneralErrorMessage, updatingProfile, isMutating])

  // Consider the component loading if either data is loading or any mutation is in progress
  const isLoading = loading || isMutating || updatingProfile

  const [currentMatchesPage, setCurrentMatchesPage] = useState(1)
  const {
    data: matches,
    isLoading: loadingMatches,
    error: matchesError,
  } = useProfileMatches(id, currentMatchesPage)

  return (
    <div
      className={`flex flex-col flex flex-grow sm:flex-row flex w-full ${
        isLoading ? 'opacity-50' : ''
      }`}
    >
      <Helmet>
        <title>
          {profile?.lastName
            ? `${profile?.firstName} ${profile?.lastName}`
            : 'Profile'}{' '}
          - MCSS Pro Connect
        </title>
      </Helmet>

      <UserProfileNavigationDrawer />
      <Layout.PaddedContent className="overflow-y-auto">
        <div className="flex-col flex sm:flex-row flex pb-32">
          <div className="mr-0 sm:mr-4 w-full flex-col flex">
            <Content.Body>
              <Navigation.ReturnLink to={`/admin/profiles${locationSearch}`}>
                Return to Profiles
              </Navigation.ReturnLink>
            </Content.Body>
            <Content.Heading>
              {loading ? (
                <div>
                  <div className="mr-auto">
                    <Indicators.Spinner />
                  </div>
                </div>
              ) : (
                `${profile?.firstName} ${profile?.lastName}`
              )}
            </Content.Heading>
            {profileError ? (
              <p>
                {profileError instanceof Error
                  ? profileError.message
                  : 'Failed to load profile'}
              </p>
            ) : (
              ''
            )}
            {mutationError ? (
              <p>
                {mutationError instanceof Error
                  ? mutationError.message
                  : 'An error occurred'}
              </p>
            ) : (
              ''
            )}
            {generalErrorMessage ? <p>{generalErrorMessage}</p> : ''}
            {profile?.status === 'PENDING_COMPLETION' ? (
              <Section>
                <Attribute label="Invitation Url:">
                  <div className="w-full">
                    <p ref={inviteUrlRef}>{profile?.invitationUrl ?? ''}</p>
                    <div className="ml-2">
                      <Navigation.IconButton
                        icon={faClipboard}
                        onClick={handleCopyInvite}
                      />
                    </div>
                  </div>
                </Attribute>
              </Section>
            ) : null}

            <Section>
              <Attribute
                label="Status:"
                value={loading ? '-------------' : profile?.status}
              />
              <Attribute
                label="Signup Date:"
                value={DateTime.fromISO(profile?.createdAt || '').toFormat(
                  'MMM, dd, yyyy'
                )}
              />
            </Section>

            <Section>
              <Attribute
                label="Email Address:"
                value={loading ? '-------------' : profile?.emailAddress}
              />
              <Attribute
                label="Profile Type:"
                value={
                  loading
                    ? '-------------'
                    : profile?.role === 'EDUCATOR'
                    ? 'Educator'
                    : 'Industry Professional'
                }
              />
            </Section>

            {profile?.connectionPreference ? (
              <Section>
                <Attribute
                  label="Connection Preference:"
                  value={
                    loading ? '-------------' : profile?.connectionPreference
                  }
                />
              </Section>
            ) : null}

            <Section>
              {profile?.role === 'EDUCATOR' ? (
                <Attribute label="School:" value={profile.schoolName} />
              ) : (
                <Attribute label="Job Field:" value={profile?.jobField} />
              )}
              {profile?.role === 'EDUCATOR' ? (
                <Attribute label="District:" value={profile?.districtName} />
              ) : (
                <Attribute label="Company Name:" value={profile?.companyName} />
              )}
            </Section>

            <Section>
              {profile?.secondaryLanguages ? (
                <Attribute
                  label="Languages:"
                  value={(profile?.secondaryLanguages ?? '') as string[]}
                />
              ) : null}

              {profile?.gradeLevels ? (
                <Attribute
                  label="Grade Levels:"
                  value={(profile?.gradeLevels ?? '') as string[]}
                />
              ) : null}
            </Section>

            {profile?.role === 'PROFESSIONAL' ? (
              <Section>
                {profile?.preferredActivities ? (
                  <Attribute
                    label="Activities:"
                    value={(profile as any).preferredActivities}
                  />
                ) : null}

                {profile?.cities ? (
                  <Attribute label="Cities:" value={(profile as any).cities} />
                ) : null}
              </Section>
            ) : (
              <span />
            )}

            <Section>
              {profile?.about ? (
                <Attribute label="About" value={profile?.about} noTruncate />
              ) : null}
            </Section>

            <Section>
              {profile?.publicBio ? (
                <Attribute
                  label="Public Bio"
                  value={profile?.publicBio}
                  noTruncate
                />
              ) : null}
            </Section>

            <Section>
              {profile?.collaboratorDescription ? (
                <Attribute
                  label="Collaborator Statement"
                  value={profile?.collaboratorDescription}
                  noTruncate
                />
              ) : null}
            </Section>

            <Section>
              {profile?.contentDescription ? (
                <Attribute
                  label="Content Description"
                  value={profile?.contentDescription}
                  noTruncate
                />
              ) : null}
            </Section>

            {profile?.contentAreas ? (
              <Section>
                <Attribute
                  label="Keywords"
                  value={(profile?.keywords ?? ['']) as string[]}
                />
              </Section>
            ) : null}

            {profile?.status !== 'NEEDS_APPROVAL' ? (
              <React.Fragment>
                <span className="my-4 border-top border-stone-200 w-full block" />
                <Section>
                  <Attribute label="Approved By:" value={profile?.approvedBy} />
                  <Attribute label="Approved On:" value={profile?.approvedAt} />
                </Section>
              </React.Fragment>
            ) : (
              <span />
            )}

            <MatchesList
              matches={matches}
              isLoading={loadingMatches}
              error={matchesError}
              onPageChange={(page) => setCurrentMatchesPage(page)}
            />
          </div>
          <div className="flex-col flex mt-2 md:mt-4 mb-auto w-full sm:w-1/2 md:w-1/3">
            {profile?.status !== 'NEEDS_APPROVAL' ? (
              <React.Fragment>
                <div className="mx-auto mb-2">
                  {loading ? (
                    <Indicators.Spinner size="4x" />
                  ) : (
                    <Avatar.Simple
                      name={`${profile?.firstName} ${profile?.lastName}`}
                      maxInitials={2}
                      color={profile?.color ?? randomColor()}
                      imageUrl={profile?.avatarUrl || ''}
                      size={128}
                    />
                  )}
                </div>
                <Content.ActionButton
                  className="mt-0"
                  label="Edit"
                  icon={faPencil}
                  onClick={handleEditClick}
                  loading={updatingProfile}
                />
              </React.Fragment>
            ) : (
              <span />
            )}
            {profile?.status === 'NEEDS_APPROVAL' ? (
              <React.Fragment>
                <Content.ActionButton
                  className="mt-0"
                  label="Approve"
                  icon={faThumbsUp}
                  onClick={handleApproveClick}
                  loading={updatingProfile}
                />
                <Content.ActionButton
                  label="Deny"
                  icon={faBan}
                  appearance="destructive"
                  onClick={handleDenyClick}
                  loading={updatingProfile}
                />
              </React.Fragment>
            ) : (
              <span />
            )}
            {profile?.status === 'PENDING_COMPLETION' ? (
              <Content.ActionButton
                label="Re-invite"
                icon={faEnvelope}
                onClick={handleApproveClick}
                loading={updatingProfile}
              />
            ) : (
              <span />
            )}

            {!['SUSPENDED', 'NEEDS_APPROVAL', 'DEACTIVATED'].includes(
              profile?.status as string
            ) ? (
              <Content.ActionButton
                label="Suspend Profile"
                icon={faBan}
                onClick={handleSuspendClick}
                appearance="destructive"
                loading={updatingProfile}
              />
            ) : (
              <span />
            )}

            {!['DEACTIVATED', 'NEEDS_APPROVAL'].includes(
              profile?.status as string
            ) ? (
              <Content.ActionButton
                label="Deactivate Profile"
                icon={faTrashAlt}
                onClick={handleDeactivateClick}
                appearance="destructive"
                loading={updatingProfile}
              />
            ) : (
              <span />
            )}

            {['DEACTIVATED', 'SUSPENDED'].includes(
              profile?.status as string
            ) ? (
              <React.Fragment>
                <Content.ActionButton
                  appearance="accent"
                  className="mt-3"
                  label="Re-Activate"
                  icon={faBolt}
                  onClick={handleActivateClick}
                  loading={updatingProfile}
                />
                {profile?.status === 'DEACTIVATED' ? (
                  <Content.ActionButton
                    label="Delete"
                    icon={faTrashAlt}
                    onClick={handleDestroyClick}
                    appearance="destructive"
                    loading={updatingProfile}
                  />
                ) : null}
              </React.Fragment>
            ) : (
              <span />
            )}

            {/* Add role management section */}
            {userInfo && !loadingUser ? (
              <div className="flex-col flex mt-4 w-full">
                <Content.Heading>Role Management</Content.Heading>
                <Section>
                  <Attribute
                    label="Access Level:"
                    value={userInfo.accessLevel}
                  />
                </Section>

                {isCurrentlyAdmin ? (
                  <Content.ActionButton
                    label="Demote to User"
                    icon={faUserMinus}
                    onClick={showDemoteConfirmation}
                    appearance="destructive"
                    loading={isRoleMutating}
                  />
                ) : (
                  <Content.ActionButton
                    label="Promote to Admin"
                    icon={faUserPlus}
                    onClick={showPromoteConfirmation}
                    appearance="actionable"
                    loading={isRoleMutating}
                  />
                )}
              </div>
            ) : null}
          </div>
        </div>

        {/* Error display for user lookup */}
        {userError ? (
          <div className="text-red border-red-600 bg-red-200 p-2 rounded">
            {userError instanceof Error ? userError.message : 'Unknown error'}
          </div>
        ) : null}

        {confirmId ? (
          <Modal.Dialog
            title="Are You Sure?"
            message="Deleting this profile will permenantly delete the profile and associated user account from the system. You cannot undo this action."
            onDismiss={dismissModal}
            onConfirm={confirmDestroyProfile}
            loading={isLoading}
            destructive={true}
          />
        ) : null}
        {showDenyModal ? (
          <UserProfileDenyView
            profileId={id}
            onDismiss={handleDenyModalDismiss}
          />
        ) : null}

        {/* Add role change confirmation modal */}
        {confirmRoleAction && userInfo ? (
          <Modal.Dialog
            title={
              confirmRoleAction === 'promote'
                ? 'Promote to Admin?'
                : 'Demote to User?'
            }
            message={
              confirmRoleAction === 'promote'
                ? `Are you sure you want to promote ${
                    profile?.emailAddress ?? 'this user'
                  } to admin? This will grant them full administrative access to the system.`
                : `Are you sure you want to demote ${
                    profile?.emailAddress ?? 'this user'
                  } from admin? They will lose all administrative privileges.`
            }
            onDismiss={dismissRoleModal}
            onConfirm={handleConfirmRoleChange}
            destructive={confirmRoleAction === 'demote'}
            loading={isRoleMutating || updatingProfile}
            confirmLabel={
              confirmRoleAction === 'promote' ? 'Promote' : 'Demote'
            }
          />
        ) : null}
      </Layout.PaddedContent>
    </div>
  )
}
